/* eslint-disable max-len */
import React, { useCallback, useMemo } from 'react';
import { useSessionStorage } from 'react-use';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Enum } from '../../../platform/Enum';
import { ColorButton } from '../../../ui/ColorButton';
import { KeepPopoutWidgetOpenButton } from '../../../ui/KeepOpenButton';
import { Position } from '../../../ui/Position';
import { useResolve } from '../../Container';
import { toolEquip } from '../../canvas/tooling/ToolCommands';
import { ToolManager } from '../../canvas/tooling/ToolManager';
import { ElementSelector } from '../../canvas/ui/ElementSelector';
import { PopoutWidget } from '../../canvas/workspace/widgets/WidgetInterface';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { ColorGroup, Colors, remInPx } from './../../../ui';
import { ShapeAlgorithm } from './ShapeAlgorithm';
import { ShapeKind, ShapeStyle } from './ShapeEnums';

const { ItemGroup, ItemSet, Item, ItemPreview } = ElementSelector;

// const ShapePreview = React.memo<{ width: number, height: number, kind: ShapeKind, style: ShapeStyle, color: string, sides: number }>((props) => {
//     const { width, height, kind, style, color, sides } = props;
//     const alg = ShapeAlgorithm.for(kind);
//     const size = remInPx(4);
//     const stroke = 2;
//     const data = alg({ color, cornerRadius: 2, stroke, style, sides, width: size * (width / height), height: size });
//     return <Flexer padding=".125 0">
//         <svg width={size * (width / height)} height={size}>
//             {data.map(({ type, ...props }, i) =>
//                 React.createElement(type, { key: `${type}/${i}`, ...props })
//             )}
//         </svg>
//     </Flexer>;
// });

const ShapePreview = React.memo<{ width: number, height: number, kind: ShapeKind, style: ShapeStyle, color: string, sides: number }>((props) => {
    const { width, height, kind, style, color, sides } = props;
    const padding = 10;
    const alg = ShapeAlgorithm.for(kind);
    const shape = {
        color,
        cornerRadius: 2,
        stroke: 2,
        style,
        sides,
        width: remInPx((5 * width) + (.625 * (width - 1))),
        height: remInPx(5 * height)
    };
    const data = alg(shape);
    const applyShadow = Colors.isLight(color, 4);
    const svgStyle = {
        position: 'absolute' as const,
        left: -padding,
        top: -padding,
        width: shape.width + (padding * 2),
        height: shape.width + (padding * 2),
    };
    return <Flexer relative width={5 * width} height={5 * height}>
        <svg style={svgStyle}>
            {applyShadow && <defs>
                <filter id="shadow" colorInterpolationFilters="sRGB" x="-50%" y="-50%" width="200%" height="200%">
                    <feDropShadow dx="0" dy="0" stdDeviation="2" floodOpacity="0.2" />
                </filter>
            </defs>}
            <g transform={`translate(${padding} ${padding})`}>
                {data.paths.map(({ d, ...props }, i) => <path key={i} d={d.toString()} {...props} filter={applyShadow ? 'url(#shadow)' : undefined} />)}
            </g>
        </svg>
    </Flexer>;
});

const ArrowPreview = React.memo<{ kind: ShapeKind, style: ShapeStyle, color: string }>(({ kind, style, color }) => {
    const alg = ShapeAlgorithm.for(kind);
    const size = remInPx(4);
    const stroke = kind === ShapeKind.arrow1 ? 30 : 2;
    const data = alg({ color, cornerRadius: 2, stroke, style, sides: 0, width: size * 2, height: size });
    const applyShadow = Colors.isLight(color, 4);
    return <svg width={size * 2} height={size}>
        {applyShadow && <defs>
            <filter id="shadow" colorInterpolationFilters="sRGB" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="0" stdDeviation="2" floodOpacity="0.2" />
            </filter>
        </defs>}
        {data.paths.map(({ d, ...props }, i) => <path key={i} d={d.toString()} {...props} filter="url(#shadow)" />)}
    </svg>;
});

const id = (...x: string[]) => x.join('/');

const ShapeItem = (kind: ShapeKind, style: ShapeStyle, color: string, sides = 4) => (
    Item(id(`${Enum.label(ShapeKind, kind)}/${sides}`, Enum.label(ShapeStyle, style)), null, { kind, style, color, sides, width: 1, height: 1 }, ItemPreview(ShapePreview))
);

const DoubleShapeItem = (kind: ShapeKind, style: ShapeStyle, color: string, sides = 4) => (
    Item(id(`${Enum.label(ShapeKind, kind)}/${sides}`, Enum.label(ShapeStyle, style)), null, { kind, style, color, sides, width: 2, height: 1 }, ItemPreview(ShapePreview, 2))
);

const ArrowItem = (kind: ShapeKind, style: ShapeStyle, color: string, stroke = 2) => (
    Item(id(`${Enum.label(ShapeKind, kind)}`, Enum.label(ShapeStyle, style)), null, { kind, style, color, stroke, width: 2, height: 1 }, ItemPreview(ArrowPreview))
);

const BraceItem = (kind: ShapeKind, style: ShapeStyle, color: string, stroke = 2) => (
    Item(id(Enum.label(ShapeKind, kind), Enum.label(ShapeStyle, style)), null, { kind, style, color, stroke, width: 1, height: 2 }, ItemPreview(ArrowPreview))
);

const shapes = [
    ShapeKind.rectangle,
    ShapeKind.oval,
    ShapeKind.triangle,
    ShapeKind.rightangle,
    ShapeKind.rhombus,
    ShapeKind.pentagon,
    ShapeKind.hexagon,
    ShapeKind.octagon,
    ShapeKind.paragram,
    ShapeKind.heart,
    ShapeKind.quote,
    ShapeKind.trapezoid,
];

const banners = [
    ShapeKind.banner1,
    ShapeKind.banner2,
    ShapeKind.banner4,
    ShapeKind.banner3,
    ShapeKind.banner5,
    ShapeKind.banner6,
];

const stars = [
    3, 4, 5, 6, 8, 12
];

const badges = [
    3, 4, 5, 6, 8, 12
];

const braces = [
    ShapeKind.brace,
    ShapeKind.squareBrace
];

const createModel = (color: string) => [
    ItemGroup('shapes', 'Shapes', 'deqo://icons/d2/shapes@[0.8,0.8,0]', [
        ItemSet('shapes', 'Shapes', 4, [
            ...shapes.map(k => ShapeItem(k, ShapeStyle.fill, color)),
        ]),
        ItemSet('banners', 'Banners', 4, [
            ...banners.flatMap(k => DoubleShapeItem(k, ShapeStyle.fill, color)),
        ]),
    ]),
    ItemGroup('stars', 'Stars', 'deqo://icons/hero/star@[0.8,0.8,0]', [
        ItemSet('Stars', 'Stars', 4, [
            ...stars.map(x => ShapeItem(ShapeKind.star, ShapeStyle.fill, color, x)),
            // ShapeItem(ShapeKind.cloud, ShapeStyle.fill, color),
        ]),
        ItemSet('Badges', 'Badges', 4, [
            ...badges.map(x => ShapeItem(ShapeKind.badge, ShapeStyle.fill, color, x)),
            // ShapeItem(ShapeKind.cloud, ShapeStyle.fill, color),
        ]),
    ]),
    ItemGroup('arrows', 'Arrows', 'deqo://icons/hero/arrow-right@[0.8,0.8,0]', [
        ItemSet('arrows', 'Arrows', 2, [
            ArrowItem(ShapeKind.arrow1, ShapeStyle.fill, color, 30),
            ArrowItem(ShapeKind.arrow2, ShapeStyle.fill, color, 30),
            ArrowItem(ShapeKind.arrow3, ShapeStyle.fill, color, 30),
            ArrowItem(ShapeKind.arrow4, ShapeStyle.fill, color, 30),
        ]),
    ]),
    // ItemGroup('braces', 'Braces', 'deqo://icons/hero/arrow-right@[0.8,0.8,0]', [
    //     ItemSet('braces', 'Braces', 2, [
    //         ArrowItem(ShapeKind.brace, ShapeStyle.fill, color, 30),
    //         ArrowItem(ShapeKind.squareBrace, ShapeStyle.fill, color, 30),
    //     ]),
    // ]),
];

export const ShapeWidget = React.memo<{ widget: PopoutWidget }>(({ widget }) => {

    const dispatch = useResolve(CommandDispatcher);
    const tools = useResolve(ToolManager);
    const [color, setColor] = useSessionStorage('deqo/ui/shapeWidget/color', ColorGroup.default[3]);

    const onSelect = useCallback((item: any, selectMode: 'drag' | 'click') => {
        widget.update({ open: !widget.autoClose });

        const mode = selectMode === 'click' ? 'drag' : 'drop';

        tools.setSettings('shapeTool', { ...item.props, color });
        dispatch(toolEquip, ['shapeTool', `${mode}`]);

    }, [color]);

    const model = useMemo(() => createModel(color), [color]);

    return <Flexer relative background="white" width="24">
        <ElementSelector name="shapes" model={model} onSelectItem={onSelect} />
        <Position inset=".6" at="topRight">
            <Flexer flex="row" align="center" gap=".5">
                <ColorButton value={color} onChange={setColor} autoSelect groups />
                <KeepPopoutWidgetOpenButton widget={widget} />
            </Flexer>
        </Position>
    </Flexer>;
});
